import { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { useAppSelector } from '../../../../../state/hooks';
import {
  getPaymentPartnerType,
  getSelectedVehicleId,
} from '../../../state/onboardingUiSlice';
import { useCallback } from 'react';
import { routeStorage } from '../../../../../routes/routeStorage';
import { CustomFormattedMessage } from '../../../../../i18n/i18n';
import Button from '@rio-cloud/rio-uikit/Button';
import {
  PacePaymentPartners,
  usePaceOnboardingQuery,
  usePacePaymentMethodsQuery,
} from '../../../../../state/paymentPartners/paymentPartnersApiTemplateSlice';
import { getUser } from '../../../../../login/loginSlice';
import {
  ExternalAuthSources,
  ExternalOnboardingStatus,
} from '../../../../RedirectPage';
import { PaceCardRepresentation } from './PaceCardRepresentation';
import { PaymentPartnerType } from '../../../../../codegen/vehicles';
import { CardSelectionDropdown } from './CardSelectionDropdown';
import { formatExpiryForDisplay } from '../../../../../utils/dates';

type PacePaymentPartnerInfo = {
  name: PacePaymentPartners;
  externalPageName: string;
};

const pacePaymentPartnersInfo: Record<string, PacePaymentPartnerInfo> = {
  [PaymentPartnerType.DkvPayment]: {
    name: 'DKV',
    externalPageName: 'DKV Cockpit',
  },
};

export const PaceCardForm = () => {
  const selectedVehicleId = useAppSelector(getSelectedVehicleId);
  const user = useAppSelector(getUser);
  const selectedPaymentPartner = useAppSelector(getPaymentPartnerType);
  const pacePaymentPartner = pacePaymentPartnersInfo[selectedPaymentPartner];

  const paceRedirectUrl = (status: ExternalOnboardingStatus) =>
    `${window.location.origin}/redirect?source=${ExternalAuthSources.PACE}&paymentPartner=${selectedPaymentPartner}&externalOnboardingStatus=${status}`;

  const onboardingQuery = usePaceOnboardingQuery({
    cancelRedirectUrl: paceRedirectUrl('CANCELLED'),
    failureRedirectUrl: paceRedirectUrl('FAILED'),
    successRedirectUrl: paceRedirectUrl('SUCCESS'),
    kind: pacePaymentPartner.name,
    metadata: {},
    proxy: false,
    userId: user.accountId,
  });

  const clientOnboardingUrl = onboardingQuery.data?.data.clientOnboardingUrl;

  const cardsQuery = usePacePaymentMethodsQuery();

  const availableCards = cardsQuery.data?.items || [];

  const cardOptions: SelectOption[] = availableCards
    .filter((card) => card.label === pacePaymentPartner.name && card.expiresAt)
    .map((card) => {
      const convertedDate = formatExpiryForDisplay(card.expiresAt!);

      return {
        id: card.id,
        label: (
          <PaceCardRepresentation
            cardName={card.name}
            paymentPartner={selectedPaymentPartner}
            expiryDate={convertedDate}
          />
        ),
      };
    });

  const onClick = useCallback(() => {
    routeStorage.saveVehicleId(selectedVehicleId ?? '');
    if (clientOnboardingUrl) {
      window.open(clientOnboardingUrl, '_self');
    }
  }, [selectedVehicleId, clientOnboardingUrl]);

  return (
    <>
      <CardSelectionDropdown
        isLoading={cardsQuery.isLoading}
        options={cardOptions}
        errorContent={
          cardsQuery.error ? (
            <CardsErrorMessage
              paymentPartnerInfo={pacePaymentPartner}
              errorStatusCode={cardsQuery.error.status}
            />
          ) : null
        }
        noCardsContent={
          !cardsQuery.error && cardOptions.length === 0 ? (
            <NoCardsFoundMessage paymentPartner={pacePaymentPartner.name} />
          ) : null
        }
      />

      {!onboardingQuery.isLoading && (
        <div className="col-xs-12">
          <div>
            <label>
              <CustomFormattedMessage
                id={
                  'onboarding.dialog.serviceCardForm.serviceCardType.connectNewCard'
                }
                values={{ paymentPartner: pacePaymentPartner.name }}
              />
            </label>
          </div>
          <div>
            {onboardingQuery.error ? (
              <div
                data-testid={'onboarding-dialog-pace-error-section'}
                className={'text-color-danger'}
              >
                <CustomFormattedMessage
                  id={
                    'onboarding.dialog.serviceCardForm.pace.onboardingTechnicalError'
                  }
                  values={{ paymentPartner: pacePaymentPartner.name }}
                />
              </div>
            ) : (
              <Button
                data-testid="onboarding-dialog-body-pace-onboarding-button"
                className={'margin-bottom-15'}
                onClick={onClick}
                bsStyle="primary"
                disabled={onboardingQuery.isError}
              >
                {pacePaymentPartner.externalPageName}
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const CardsErrorMessage = ({
  paymentPartnerInfo,
  errorStatusCode,
}: {
  errorStatusCode: number;
  paymentPartnerInfo: PacePaymentPartnerInfo;
}) => (
  <CustomFormattedMessage
    id={
      errorStatusCode === 401
        ? 'onboarding.dialog.serviceCardForm.cardList.unauthenticatedError'
        : 'onboarding.dialog.serviceCardForm.cardList.technicalError'
    }
    values={{
      paymentPartner: paymentPartnerInfo.name,
      paymentPartnerPage: paymentPartnerInfo.externalPageName,
    }}
  />
);

const NoCardsFoundMessage = ({
  paymentPartner,
}: {
  paymentPartner: PacePaymentPartners;
}) => (
  <CustomFormattedMessage
    id={'onboarding.dialog.serviceCardForm.pace.noCards'}
    values={{ paymentPartner }}
  />
);
